#import "src/fragments/tagFragments.graphql"
#import "src/queries/fragments/teamBudgetsFragment.graphql"

fragment BaseTeamFragment on Group {
  id
  isHidden
  name
  hierarchyIds
  parentIds
  type
  attributes {
    id
    attributeType
    subtype
    label
    readonly
    value
    # Disable Apollo caching for this group attributes
    __typename @skip(if: true)
  }
}

fragment ObjectiveGroup on Group {
  id
  name
  type
  objectiveIds
}

fragment BaseTeamParentFragment on Group {
  id
  name
}

mutation createTeamInAllocation(
  $parentId: String!
  $name: String!
  $type: String!
) {
  team: createTeam(parentId: $parentId, name: $name, type: $type) {
    ...BaseTeamFragment
    hierarchy
    memberCount
  }
}

mutation markGroupAsHidden($id: String!, $isHidden: Boolean) {
  team: markGroupAsHidden(id: $id, isHidden: $isHidden) {
    id
    name
    isHidden
  }
}

mutation RemoveTeamSubtreeFromOrg(
  $rootGroupId: String!
  $allocationProjectId: String
) {
  removeTeamSubtreeFromOrg(
    rootGroupId: $rootGroupId
    allocationProjectId: $allocationProjectId
  ) {
    rootGroupId
    hiddenGroupIds
    membershipsEndedCount
    allocationsClearedCount
  }
}

query getParentGroupsForNewTeam($groupId: String!, $groupTypes: [String]!) {
  groups: getGroup(groupId: $groupId) {
    id
    name
    type
    descendantGroups(groupTypes: $groupTypes) {
      id
      name
      hierarchy
      type
    }
  }
}

query getDescendantGroups($groupIds: [String]!, $supplySourceTypes: [String]) {
  groups: getGroups(groupIds: $groupIds) {
    ...BaseTeamFragment
    ...TeamBudgetsFragment
    descendantGroups(groupTypes: $supplySourceTypes) {
      ...BaseTeamFragment
      ...TeamBudgetsFragment
    }
  }
}

query searchTeams($search: [String], $groupTypes: [String]) {
  filteredGroups: listGroups(
    search: $search
    groupTypes: $groupTypes
    includeHiddenGroups: false
    limit: 10
  ) {
    groups {
      ...BaseTeamFragment
      ...TeamBudgetsFragment
    }
  }
}

query getObjectivesGroups($valueGraphQueryOptions: ValueGraphQueryOptions) {
  groups: getGroupsWithImportedObjectives(
    eagerLoadParents: true
    groupKinds: ["team"]
    filter: $valueGraphQueryOptions
  ) {
    ...ObjectiveGroup
    hierarchyIds
    objectives(valueGraphQueryOptions: $valueGraphQueryOptions) {
      id
      name
      type
      kind
      attributes {
        subtype
        label
        value
        attributeType
      }
      datasource {
        id
        type
      }
    }
  }
}

query getObjectives($groupIds: [String]) {
  groups: getGroups(groupIds: $groupIds) {
    id
    name
    parentObjectiveIds
    type
    kind
    datasource {
      id
      type
    }
    attributes {
      id
      attributeType
      subtype
      label
      value
    }
  }
}

query getSourceGroups($groupIds: [String]) {
  sourceGroups: getGroups(groupIds: $groupIds) {
    id
    name
    type
    parentIds
    hierarchy
    hierarchyIds
  }
}

mutation addAssociatedGroup(
  $groupId: ID!
  $targetId: ID!
  $allocationProjectId: ID!
) {
  group: addAssociatedGroup(
    groupId: $groupId
    targetId: $targetId
    allocationProjectId: $allocationProjectId
  ) {
    id
  }
}

mutation removeAssociatedGroup(
  $groupId: ID!
  $targetId: ID!
  $allocationProjectId: ID!
) {
  group: removeAssociatedGroup(
    groupId: $groupId
    targetId: $targetId
    allocationProjectId: $allocationProjectId
  ) {
    id
  }
}

mutation setObjectiveVerifiedFor($groupId: ID!, $allocationProjectId: String!) {
  result: setObjectiveVerifiedFor(
    groupId: $groupId
    allocationProjectId: $allocationProjectId
  )
}

query listTeamsInAllocation(
  $search: [String]
  $typeFilters: [String]
  $kindFilters: [String]
  $limit: Int
) {
  result: listGroups(
    search: $search
    groupTypes: $typeFilters
    groupKinds: $kindFilters
    limit: $limit
    includeHiddenGroups: false
  ) {
    groups {
      id
      parentIds
      name
      type
      hierarchy
      hierarchyIds
      isHidden
    }
  }
}

query listSupplyGroups($search: [String], $typeFilters: [String], $limit: Int) {
  result: listGroups(
    search: $search
    groupTypes: $typeFilters
    limit: $limit
    includeHiddenGroups: false
  ) {
    groups {
      id
      name
      type
      parentIds
      hierarchy
      memberCount
      hierarchyIds
    }
  }
}

query getHierarchy($groupId: ID!, $parentIds: [String]) {
  result: getHierarchy(groupId: $groupId, parentIds: $parentIds) {
    id
    hierarchy
  }
}

query getHierarchyV2($groupId: String!) {
  result: getGroup(groupId: $groupId) {
    id
    name
    type
    parentIds
    hierarchyIds
    hierarchy
  }
}

query getGroupAttributes($groupId: String!) {
  result: getGroup(groupId: $groupId) {
    id
    attributes {
      id
      attributeType
      subtype
      label
      readonly
      value
    }
  }
}

mutation moveToNewParent(
  $groupId: ID!
  $parentGroupId: ID!
  $displayValue: String
) {
  result: addAssociationV2(
    input: {
      entityAId: $groupId
      entityBId: $parentGroupId
      type: NEW_PARENT
      displayValue: $displayValue
      scopeId: null
      providerEntityId: $parentGroupId
    }
  ) {
    id
    tagId
  }
}

mutation revertMovedGroup($groupId: ID!, $parentGroupId: ID!) {
  result: removeAssociation(
    input: { entityAId: $groupId, entityBId: $parentGroupId, type: NEW_PARENT }
  ) {
    id
    tagId
  }
}

query getGroups($groupIds: [String]) {
  groups: getGroups(groupIds: $groupIds) {
    id
    name
    type
    hierarchyIds
  }
}

# Batched secondary lookup of a group's featured/badge tags, kept separate
# from the main bulk Planner/Forecast tree query (DynamicMacroAllocationGroupFragment)
# so that heavy hot-path query isn't loading tags for every group on every
# request. Group.tags is DataLoader-batched server-side, so fetching for many
# ids at once here (see useLoadGroupTags) stays a single request.
query getGroupTags($groupIds: [String]) {
  groups: getGroups(groupIds: $groupIds) {
    id
    tags: appliedTags {
      id
      tagId
      type
      displayValue
      isActive
    }
  }
}

query getTeam($teamId: String!) {
  result: getGroup(groupId: $teamId) {
    ...BaseTeamFragment
  }
}
